import { useEffect, useState } from "react";
import { Download, ArrowLeft, FileText, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function ApiFaq() {
  const [sections, setSections] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api-faq");
        if (!cancelled) setSections(data.sections || []);
      } catch (e) {
        if (!cancelled)
          setErr(e?.response?.data?.detail || e.message || "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pdfHref = `${process.env.REACT_APP_BACKEND_URL || ""}/api/api-faq.pdf`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="api-faq-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            data-testid="api-faq-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>

          <header className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                For developers
              </p>
              <h1
                className="mt-2 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
                data-testid="api-faq-heading"
              >
                Partner API guide
              </h1>
              <p className="mt-2 text-base text-slate-600 max-w-2xl">
                How to connect your app or partner portal to The AI Took My Job
                in 15 minutes. Issued keys, scopes, endpoints, signed webhooks.
              </p>
            </div>
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
              data-testid="api-faq-download-pdf"
            >
              <Download className="h-4 w-4" /> Download PDF
            </a>
          </header>

          {err ? (
            <div
              className="mt-10 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700"
              data-testid="api-faq-error"
            >
              {String(err)}
            </div>
          ) : !sections ? (
            <div
              className="mt-10 flex items-center gap-2 text-sm text-slate-500"
              data-testid="api-faq-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading guide…
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-4 gap-10">
              <aside className="lg:col-span-1 hidden lg:block">
                <nav className="sticky top-28 border-l border-slate-200 pl-4 space-y-2">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-slate-600 hover:text-slate-900"
                      data-testid={`api-faq-toc-${s.id}`}
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </aside>

              <article className="lg:col-span-3 space-y-10" data-testid="api-faq-content">
                {sections.map((s) => (
                  <section key={s.id} id={s.id} className="scroll-mt-28">
                    <h2
                      className="text-xl font-medium text-slate-900 tracking-tight"
                      data-testid={`api-faq-section-${s.id}`}
                    >
                      {s.title}
                    </h2>
                    <div className="mt-3 space-y-3">
                      {(s.blocks || []).map((b, idx) => {
                        const [kind, text] = b;
                        if (kind === "code") {
                          return (
                            <pre
                              key={`${s.id}-${idx}`}
                              className="rounded-xl bg-slate-950 text-slate-100 p-4 text-xs leading-relaxed overflow-x-auto"
                            >
                              <code>{text}</code>
                            </pre>
                          );
                        }
                        return (
                          <p
                            key={`${s.id}-${idx}`}
                            className="text-sm text-slate-600 leading-relaxed"
                          >
                            {text}
                          </p>
                        );
                      })}
                    </div>
                  </section>
                ))}

                <section className="rounded-2xl border border-slate-200 bg-white p-6 flex items-start gap-4">
                  <FileText className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      Want a copy for your engineering team?
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      Grab the same content as a printable PDF — share it with
                      stakeholders or store it next to your API key in the team
                      vault.
                    </p>
                    <a
                      href={pdfHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      data-testid="api-faq-download-pdf-secondary"
                    >
                      <Download className="h-3.5 w-3.5" /> Download PDF
                    </a>
                  </div>
                </section>
              </article>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
